import React, { useEffect, useState, useCallback } from 'react';
import {
  Play,
  Pause,
  XCircle,
  RotateCcw,
  Download,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Filter,
  RefreshCw,
  ListFilter,
  Layers,
  FileSpreadsheet,
  Search
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { BulkJob, BulkJobItem, BulkJobStatus, BulkJobType } from '../../lib/jobs/types';

interface BulkJobsDashboardProps {
  initialJobId?: string | null;
}

export const BulkJobsDashboard: React.FC<BulkJobsDashboardProps> = ({ initialJobId }) => {
  const [jobs, setJobs] = useState<BulkJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<BulkJob | null>(null);
  const [jobItems, setJobItems] = useState<BulkJobItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const [showLegacyPanel, setShowLegacyPanel] = useState<boolean>(false);
  const [legacyLoading, setLegacyLoading] = useState<boolean>(false);
  const [legacyPreview, setLegacyPreview] = useState<{
    totalCount: number;
    applicationsCount: number;
    workspaceBreakdown?: Record<string, number>;
    programBreakdown?: Record<string, number>;
  } | null>(null);

  const [legacyFilters, setLegacyFilters] = useState({
    includeLowConfidence: false,
    includeClosedApplications: false,
    dateFrom: '',
    dateTo: '',
    forceRescan: false,
  });

  const fetchLegacyPreview = async () => {
    setLegacyLoading(true);
    try {
      const res = await safeFetch('/api/admin/bulk-jobs/legacy-scan-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(legacyFilters),
      });
      if (res && res.success) {
        setLegacyPreview(res);
      }
    } catch (err) {
      console.error('Legacy preview error:', err);
    } finally {
      setLegacyLoading(false);
    }
  };

  const handleStartLegacyBackfill = async () => {
    setLegacyLoading(true);
    try {
      const res = await safeFetch('/api/admin/bulk-jobs/legacy-scan-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(legacyFilters),
      });
      if (res && res.success) {
        setShowLegacyPanel(false);
        setLegacyPreview(null);
        window.dispatchEvent(new CustomEvent('bulk-job-created'));
        fetchJobs();
      }
    } catch (err) {
      console.error('Legacy backfill trigger error:', err);
    } finally {
      setLegacyLoading(false);
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      const url = `/api/admin/bulk-jobs${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`;
      const res = await safeFetch(url);
      if (res && res.jobs) {
        setJobs(res.jobs);
        setSelectedJob(prev => {
          if (prev) {
            const updated = res.jobs.find((j: BulkJob) => j.id === prev.id);
            return updated || prev;
          }
          const queryJobId = initialJobId || new URLSearchParams(window.location.search).get('jobId');
          if (queryJobId) {
            const target = res.jobs.find((j: BulkJob) => j.id === queryJobId);
            return target || null;
          }
          return null;
        });
      }
    } catch (err) {
      console.error('Failed to load bulk jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, initialJobId]);

  const fetchItems = useCallback(async (jobId: string) => {
    try {
      const res = await safeFetch(`/api/admin/bulk-jobs/${jobId}/items`);
      if (res && res.items) {
        setJobItems(res.items);
      }
    } catch (err) {
      console.error('Failed to fetch job items:', err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  useEffect(() => {
    if (selectedJob?.id) {
      fetchItems(selectedJob.id);
    }
  }, [selectedJob?.id, fetchItems]);

  const handleAction = async (action: 'pause' | 'resume' | 'cancel' | 'retry-failed') => {
    if (!selectedJob) return;
    setActionLoading(true);
    try {
      await safeFetch(`/api/admin/bulk-jobs/${selectedJob.id}/${action}`, { method: 'POST' });
      await fetchJobs();
      if (selectedJob.id) await fetchItems(selectedJob.id);
    } catch (err) {
      console.error(`Failed to ${action} job:`, err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: BulkJobStatus) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</span>;
      case 'completed_with_errors':
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800"><AlertTriangle className="w-3.5 h-3.5" /> Partial Success</span>;
      case 'processing':
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 animate-pulse"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing</span>;
      case 'paused':
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-200 text-slate-700"><Pause className="w-3.5 h-3.5" /> Paused</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800"><XCircle className="w-3.5 h-3.5" /> Failed</span>;
      case 'cancelled':
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700"><XCircle className="w-3.5 h-3.5" /> Cancelled</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-800"><Clock className="w-3.5 h-3.5" /> Queued</span>;
    }
  };

  const filteredJobs = jobs.filter((j) => {
    if (typeFilter !== 'all' && j.type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-6 h-6 text-blue-600" /> Bulk Operations Job Platform
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Monitor, inspect, pause, resume, or retry asynchronous background tasks across your workspace.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <Filter className="w-4 h-4" /> Filter Status:
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 bg-white font-medium focus:ring-2 focus:ring-brand-primary"
            >
              <option value="all">All Statuses</option>
              <option value="queued">Queued</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="completed_with_errors">Completed with Errors</option>
              <option value="failed">Failed</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <button
            onClick={() => setShowLegacyPanel(prev => !prev)}
            className="px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
          >
            <Layers className="w-3.5 h-3.5" /> Scan Legacy Documents
          </button>

          <button
            onClick={fetchJobs}
            className="p-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Legacy Document Backfill Control Panel */}
      {showLegacyPanel && (
        <div className="bg-white border border-indigo-200 rounded-xl p-5 shadow-sm space-y-4 animate-fade-in">
          <div className="flex items-center justify-between border-b border-indigo-100 pb-3">
            <div>
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-600" />
                Retroactive Legacy Document Scan
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Scan pre-existing legacy documents uploaded before the real-time document scanning trigger was deployed. Each scanned document records which engine read it (Document AI or a built-in engine) on the document itself.
              </p>
            </div>
            <button aria-label="Close" title="Close"
              onClick={() => setShowLegacyPanel(false)}
              className="p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-lg text-xs"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs font-semibold text-slate-700">
            <label className="flex flex-col gap-1">
              <span>Date Uploaded From</span>
              <input
                type="date"
                value={legacyFilters.dateFrom}
                onChange={(e) => setLegacyFilters({ ...legacyFilters, dateFrom: e.target.value })}
                className="border border-slate-300 rounded-lg p-2 font-normal"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span>Date Uploaded To</span>
              <input
                type="date"
                value={legacyFilters.dateTo}
                onChange={(e) => setLegacyFilters({ ...legacyFilters, dateTo: e.target.value })}
                className="border border-slate-300 rounded-lg p-2 font-normal"
              />
            </label>

            <div className="flex flex-col justify-end space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={legacyFilters.includeLowConfidence}
                  onChange={(e) => setLegacyFilters({ ...legacyFilters, includeLowConfidence: e.target.checked })}
                  className="rounded text-indigo-600 focus:ring-brand-primary"
                />
                <span>Include 'needs_manual_review' docs</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={legacyFilters.includeClosedApplications}
                  onChange={(e) => setLegacyFilters({ ...legacyFilters, includeClosedApplications: e.target.checked })}
                  className="rounded text-indigo-600 focus:ring-brand-primary"
                />
                <span>Include Closed Applications</span>
              </label>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={fetchLegacyPreview}
                disabled={legacyLoading}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {legacyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                Calculate Affected Count
              </button>
            </div>
          </div>

          {legacyPreview && (
            <div className="p-4 bg-indigo-50/60 border border-indigo-100 rounded-xl flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="text-xs font-bold text-indigo-900 block">
                  Blast Radius Pre-Flight Summary:
                </span>
                <p className="text-sm font-black text-indigo-950 mt-0.5">
                  {legacyPreview.totalCount} legacy document(s) identified across {legacyPreview.applicationsCount} application(s).
                </p>
              </div>

              <button
                onClick={handleStartLegacyBackfill}
                disabled={legacyLoading || legacyPreview.totalCount === 0}
                className="px-5 py-2 bg-brand-primary hover:bg-brand-primary-dark disabled:bg-indigo-300 text-white text-xs font-black rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-2"
              >
                {legacyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Enqueue Backfill Jobs ({legacyPreview.totalCount} items)
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-[500px]">
        {/* Job List Pane */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-slate-100 bg-slate-50/70 font-semibold text-xs text-slate-700 flex items-center gap-2">
            <ListFilter className="w-4 h-4 text-slate-500" /> Recent Jobs ({filteredJobs.length})
          </div>

          <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
            {loading && jobs.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs">Loading jobs...</div>
            ) : filteredJobs.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs">No bulk jobs found</div>
            ) : (
              filteredJobs.map((j) => (
                <div
                  key={j.id}
                  onClick={() => setSelectedJob(j)}
                  className={`p-3.5 cursor-pointer transition-colors ${
                    selectedJob?.id === j.id ? 'bg-blue-50/80 border-l-4 border-blue-600' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-semibold text-xs text-slate-900 capitalize">
                        {j.type.replace(/_/g, ' ')}
                      </span>
                      <p className="text-[11px] text-slate-500 font-mono mt-0.5">{j.id}</p>
                    </div>
                    {getStatusBadge(j.status)}
                  </div>

                  <div className="mt-3">
                    <div className="flex justify-between text-[11px] text-slate-600 mb-1">
                      <span>Progress</span>
                      <span className="font-semibold">{j.progressPercent}% ({j.completedItems + j.failedItems + j.skippedItems}/{j.totalItems})</span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          j.status === 'completed' ? 'bg-emerald-500' : j.status === 'failed' ? 'bg-rose-500' : 'bg-blue-600'
                        }`}
                        style={{ width: `${j.progressPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-2 text-[11px] text-slate-500 flex items-center justify-between">
                    <span>By: {j.createdByName || j.createdByEmail || 'Admin'}</span>
                    <span>{new Date(j.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Job Detail Pane */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          {selectedJob ? (
            <div className="flex flex-col h-full">
              {/* Job Header */}
              <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-base text-slate-900 capitalize">{selectedJob.type.replace(/_/g, ' ')}</h3>
                    {getStatusBadge(selectedJob.status)}
                  </div>
                  <p className="text-xs font-mono text-slate-500 mt-0.5">ID: {selectedJob.id} | Key: {selectedJob.idempotencyKey}</p>
                </div>

                <div className="flex items-center gap-2">
                  {selectedJob.status === 'processing' && (
                    <button
                      onClick={() => handleAction('pause')}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <Pause className="w-3.5 h-3.5" /> Pause
                    </button>
                  )}

                  {selectedJob.status === 'paused' && (
                    <button
                      onClick={() => handleAction('resume')}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <Play className="w-3.5 h-3.5" /> Resume
                    </button>
                  )}

                  {(selectedJob.status === 'queued' || selectedJob.status === 'processing' || selectedJob.status === 'paused') && (
                    <button
                      onClick={() => handleAction('cancel')}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Cancel
                    </button>
                  )}

                  {(selectedJob.failedItems > 0 || selectedJob.status === 'failed') && (
                    <button
                      onClick={() => handleAction('retry-failed')}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Retry Failed
                    </button>
                  )}

                  <a
                    href={`/api/admin/bulk-jobs/${selectedJob.id}/report`}
                    download
                    className="px-3 py-1.5 bg-brand hover:bg-brand-active text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" /> Export Report CSV
                  </a>
                </div>
              </div>

              {/* Progress Summary Metric Bar */}
              <div className="p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 border-b border-slate-100 bg-slate-50/20">
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-center">
                  <span className="text-xs text-slate-500 block">Total Items</span>
                  <span className="text-lg font-bold text-slate-900">{selectedJob.totalItems}</span>
                </div>
                <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                  <span className="text-xs text-emerald-700 block">Completed</span>
                  <span className="text-lg font-bold text-emerald-700">{selectedJob.completedItems}</span>
                </div>
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-center">
                  <span className="text-xs text-rose-700 block">Failed</span>
                  <span className="text-lg font-bold text-rose-700">{selectedJob.failedItems}</span>
                </div>
                <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <span className="text-xs text-amber-700 block">Skipped</span>
                  <span className="text-lg font-bold text-amber-700">{selectedJob.skippedItems}</span>
                </div>
                <div className="p-2.5 bg-sky-50 border border-sky-200 rounded-lg text-center">
                  <span className="text-xs text-sky-700 block">Queued/Processing</span>
                  <span className="text-lg font-bold text-sky-700">{selectedJob.queuedItems + selectedJob.processingItems}</span>
                </div>
              </div>

              {/* Items List */}
              <div className="p-3 border-b border-slate-100 bg-slate-50/70 text-xs font-semibold text-slate-700 flex justify-between items-center">
                <span>Item Level Details ({jobItems.length})</span>
                <span className="text-slate-400 font-normal">Auto-updates in background</span>
              </div>

              <div className="overflow-y-auto flex-1 p-4">
                {jobItems.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs">No item details available</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-700">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold">
                        <tr>
                          <th className="p-2.5">Item ID</th>
                          <th className="p-2.5">Status</th>
                          <th className="p-2.5">Target / Recipient</th>
                          <th className="p-2.5">Attempts</th>
                          <th className="p-2.5">Error Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                        {jobItems.map((item) => (
                          <tr key={item.id} className="hover:bg-slate-50">
                            <td className="p-2.5 font-semibold text-slate-900">{item.id}</td>
                            <td className="p-2.5 font-sans">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                                  item.status === 'completed'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : item.status === 'failed'
                                    ? 'bg-rose-100 text-rose-800'
                                    : item.status === 'skipped'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>
                            <td className="p-2.5">
                              {item.recipientEmail || item.entityId || item.applicationId || 'N/A'}
                              {item.result?.systemDecision && (
                                <div className="text-[10px] font-sans font-bold text-brand mt-0.5">
                                  Outcome: {String(item.result.systemDecision)}
                                </div>
                              )}
                            </td>
                            <td className="p-2.5">{item.attempts} / {item.maxAttempts}</td>
                            <td className="p-2.5 text-rose-600 font-sans max-w-xs truncate">
                              {item.error?.message || (item.result?.decisionRationale ? String(item.result.decisionRationale) : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-400 text-xs flex flex-col items-center justify-center flex-1">
              <Layers className="w-10 h-10 mb-2 opacity-40 text-slate-400" />
              Select a bulk job from the left pane to view progress, inspect item details, or retry failures.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
